import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronRight, Trash2, RefreshCw, CheckCircle, Clock, GraduationCap, Loader2, Printer, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { RegenerateUnitDialog } from './dialogs/RegenerateUnitDialog';
import { RegenerateLessonDialog } from './dialogs/RegenerateLessonDialog';
import { RegenerateLessonMaterialDialog } from './dialogs/RegenerateLessonMaterialDialog';
import { DeleteUnitDialog } from './dialogs/DeleteUnitDialog';
import { RemoveLessonDialog } from './dialogs/RemoveLessonDialog';
import { ThinkingAnimation } from './ThinkingAnimation';
import { Dialog, DialogContent } from '../ui/dialog';
import { PlanNoStudentsEmptyState } from './LessonsPage/components/PlanNoStudentsEmptyState';
import { formatTimeDisplay, formatIsoDateTimeShort } from '../utils/timeUtils';
import { useEvent, useApiErrors, useLazyCommand, useAuth } from '@soap/modules';
import {
    lessonsUnitElementId,
    scheduleScrollElementIntoView,
    scheduleScrollUnitCardIntoView
} from './LessonsPage/utils/lessonUnitNavigation';
import { useUnitCreationProgress } from './LessonsPage/hooks/useUnitCreationProgress';
import { isLessonCompletedDto, isLessonStarted, UnitStateKey } from './InProgressUnitsPanel/lessonStepState';
import { buildLessonPrintHtml, openLessonPrintWindow } from './InProgressUnitsPanel/printLessonDocument';
import { LessonSupportingMaterialsSection } from './InProgressUnitsPanel/LessonSupportingMaterialsSection';
import type { LessonDetailDto as PrintableLessonDto, SupportingMaterialDto } from './InProgressUnitsPanel/types';
import {
    UnitHeaderSubjectAndTopics,
    UnitHeaderSubjectLine,
} from './UnitHeaderTopicLabels';
import type { UnitHeaderTone } from './UnitHeaderTopicLabels';
import { UnitHeaderTopicsWithObjectives } from './UnitHeaderTopicsWithObjectives';
import { UnitSectionHeading } from './InProgressUnitsPanel/UnitSectionHeading';
import { UnitCardCreatedByMeta } from './UnitCardCreatedByMeta';
import { UnitCardHeaderNewBadge } from './UnitCardHeaderNewBadge';
import { deriveUnitHeaderFields } from './unitHeaderDisplay';
import {
    lessonVideoSaveWaiterKey,
    videoApprovalStateKey,
    rasterApprovalStateKey,
    applyLessonRemovedFromUnits,
    applyLessonVideoSelectionChangedToUnits,
    applyLessonRasterFigureReadyToUnits,
    applyLessonRasterFigureApprovedToUnits,
    applyLessonFigureTextOnlyAcceptedToUnits,
    mapEventRasterOptions,
} from './LessonsPage/utils/unitLessonPatches';
import { findLessonMaterialById, resolveLessonMaterials } from './InProgressUnitsPanel/instructionStepUtils';
import { useRasterGenerationStatusCheck } from './InProgressUnitsPanel/useRasterGenerationStatusCheck';
import { ExpandableInstructionStepList } from './InProgressUnitsPanel/ExpandableInstructionStepList';
import { normalizeIdPart } from './InProgressUnitsPanel/loadingStateKeys';
import { useSessionDebugMode } from '../hooks/useSessionDebugMode';
import {
    buildVideoSelectionsForLesson,
    findFirstUnapprovedMediaSlot,
    findFirstFailedVectorDiagram,
    formatUnitApprovalBlockedMessage,
    getSelectedVideoId,
    isVideoApprovedForCurrentSelection,
    isRasterFigureApproved,
    mediaSlotDomId,
    resolveStepIdForMediaCommand,
    vectorDiagramDomId,
    videoSelectionStateKey,
    type MediaReviewSlotRef,
    type PendingMediaApprovalReason,
    type UnitApprovalBlockedReason,
} from './InProgressUnitsPanel/mediaReviewUtils';
import {
    unitCardBodyClass,
    unitCardBodyMetaClass,
    unitCardExpandableTitleClass,
    unitCardHeaderClass,
    unitCardShellClass,
} from './unitCardTheme';
import { cn } from '../lib/utils';
import { confirmActionOutlineButtonClass } from '../constants/brandPalette';
import type { LearningObjectiveDto } from './InProgressUnitsPanel/types';

interface UnitDto {
    e251_UnitId?: string;
    e251_State?: string;
    e251_StudentId?: string;
    e251_Topics?: string[];
    e251_TopicIds?: string[];
    e251_Subjects?: string[];
    e251_LearningStyles?: string[];
    e251_TotalDuration?: number;
    e251_CompletionDate?: string | null;
    e251_CreatedAt?: string;
    e251_CreatedByName?: string;
    e251_Lessons?: LessonDetailDto[];
    e251_LearningObjectives?: LearningObjectiveDto[];
    e251_Touched?: boolean;
}

interface LessonDetailDto {
    e251_LessonId?: string;
    e251_LessonTitle?: string;
    e251_LessonDuration?: number;
    e251_LessonSummary?: string;
    e251_LessonMaterials?: string;
    e251_LessonState?: string;
    e251_CompletionDate?: string | null;
    e251_InstructionSteps?: PrintableLessonDto['e251_InstructionSteps'];
    e251_TopicIds?: string[];
    e251_TopicNames?: string[];
    e251_AssignedTopicFigures?: PrintableLessonDto['e251_AssignedTopicFigures'];
}

function lessonExpandKey(unitId: string, lessonId: string): string {
    return lessonVideoSaveWaiterKey(unitId, lessonId);
}

function lessonVideoStatePrefix(unitId: string, lessonId: string): string {
    return `${normalizeIdPart(unitId)}|${normalizeIdPart(lessonId)}|`;
}

function lessonIdsSignature(lessons: LessonDetailDto[] | undefined): string {
    return (lessons ?? [])
        .map(lesson => normalizeIdPart(lesson.e251_LessonId))
        .sort()
        .join(',');
}

function videoApprovedOverrideKey(unitId: string, lessonId: string, materialId: string): string {
    return videoApprovalStateKey(unitId, lessonId, materialId);
}

function sleep(ms: number): Promise<void> {
    return new Promise(resolve => window.setTimeout(resolve, ms));
}

function lessonCoversLabel(topicNames?: string[]): string | null {
    const covers = (topicNames ?? []).map(n => (n || '').trim()).filter(Boolean);
    return covers.length > 0 ? `Covers: ${covers.join(', ')}` : null;
}

interface ReviewUnitsPanelProps {
    units: UnitDto[];
    viewState: number;
    viewStates: { loading: number; ready: number };
    onApproveUnit: (unitId: string) => void;
    onDeleteUnit: (unitId: string) => void;
    onRegenerateUnit: (unitId: string) => void;
    onRefresh: () => void;
    onMarkUnitTouched?: (unitId: string) => void;
    students: Array<{ id: string; name: string }>;
    isStudentLoading: boolean;
    lessonUnitFocus?: { unitId: string; token: number } | null;
    onConsumedLessonUnitFocus?: () => void;
}

export function ReviewUnitsPanel({ 
    units, 
    viewState,
    viewStates,
    onApproveUnit, 
    onDeleteUnit, 
    onRegenerateUnit, 
    onRefresh,
    onMarkUnitTouched,
    students,
    isStudentLoading,
    lessonUnitFocus = null,
    onConsumedLessonUnitFocus
}: ReviewUnitsPanelProps) {
    const { idToken } = useAuth('review-units-print');
    const sessionDebugMode = useSessionDebugMode();
    // Local state for units - will be updated incrementally
    const [localUnits, setLocalUnits] = useState<UnitDto[]>([]);
    
    // Loading states for buttons (tracked by unitId)
    const [loadingStates, setLoadingStates] = useState<Set<string>>(new Set());
    const loadingStatesRef = useRef(loadingStates);
    
    // Thinking animation state for regeneration
    const [regeneratingUnitId, setRegeneratingUnitId] = useState<string | null>(null);
    const regeneratingUnitIdRef = useRef<string | null>(null);
    const regeneratingUnitProgress = useUnitCreationProgress(!!regeneratingUnitId);
    const runUnitFinishingStepRef = useRef(regeneratingUnitProgress?.runFinishingStep);
    runUnitFinishingStepRef.current = regeneratingUnitProgress?.runFinishingStep;
    const [regeneratingLessonKey, setRegeneratingLessonKey] = useState<string | null>(null);
    const regeneratingLessonKeyRef = useRef<string | null>(null);
    const regeneratingLessonProgress = useUnitCreationProgress(!!regeneratingLessonKey);
    const runLessonFinishingStepRef = useRef(regeneratingLessonProgress?.runFinishingStep);
    runLessonFinishingStepRef.current = regeneratingLessonProgress?.runFinishingStep;
    
    const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());
    const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());
    const [regeneratingUnit, setRegeneratingUnit] = useState<{ unitId: string; unitTitle: string } | null>(null);
    const [regeneratingLesson, setRegeneratingLesson] = useState<{
        unitId: string;
        lessonId: string;
        lessonTitle: string;
    } | null>(null);
    const [regeneratingMaterial, setRegeneratingMaterial] = useState<{
        unitId: string;
        lessonId: string;
        stepId: string;
        materialType: '10' | '20';
        materialTitle: string;
        materialId: string;
    } | null>(null);
    const [regeneratingMaterialKey, setRegeneratingMaterialKey] = useState<string | null>(null);
    const regeneratingMaterialKeyRef = useRef<string | null>(null);
    const regeneratingMaterialProgress = useUnitCreationProgress(!!regeneratingMaterialKey);
    const runMaterialFinishingStepRef = useRef(regeneratingMaterialProgress?.runFinishingStep);
    runMaterialFinishingStepRef.current = regeneratingMaterialProgress?.runFinishingStep;
    const [deletingUnit, setDeletingUnit] = useState<{ unitId: string; unitTitle: string } | null>(null);
    const [removingLesson, setRemovingLesson] = useState<{
        unitId: string;
        lessonId: string;
        lessonTitle: string;
    } | null>(null);
    const [removeLessonLoadingKeys, setRemoveLessonLoadingKeys] = useState<Set<string>>(new Set());
    
    // Track which units have been touched
    const [touchedUnits, setTouchedUnits] = useState<Set<string>>(new Set());
    const [videoSelectionOverrides, setVideoSelectionOverrides] = useState<Record<string, string | null>>({});
    const videoSaveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const [sendSaveVideoSelectionsCommand] = useLazyCommand('C322v1_SaveLessonVideoSelections');
    const [videoApprovedForVideoId, setVideoApprovedForVideoId] = useState<Record<string, string | null>>({});
    const [approvingVideoKeys, setApprovingVideoKeys] = useState<Set<string>>(new Set());
    const approvingVideoKeysRef = useRef(approvingVideoKeys);
    const lessonVideoSaveWaitersRef = useRef<Map<string, () => void>>(new Map());
    const [sendApproveLessonVideosCommand] = useLazyCommand('C323v1_ApproveLessonVideos');
    const [sendApproveLessonRasterFigureCommand] = useLazyCommand('C336v1_ApproveLessonRasterFigure');
    const [sendUploadRasterFigureCommand] = useLazyCommand('C436v1_UploadLessonRasterFigure');
    const [sendRegenerateLessonFigureCommand] = useLazyCommand('C333v1_RegenerateLessonFigure');
    const [sendAcceptLessonFigureTextOnlyCommand] = useLazyCommand('C442v1_AcceptLessonFigureTextOnly');
    const [sendGetLessonRasterFigureCommand] = useLazyCommand('C438v1_GetLessonRasterFigure');
    useRasterGenerationStatusCheck(localUnits, sendGetLessonRasterFigureCommand);
    const [rasterApprovedOverrides, setRasterApprovedOverrides] = useState<Record<string, boolean>>({});
    const [approvingRasterKeys, setApprovingRasterKeys] = useState<Set<string>>(new Set());
    const [regeneratingRasterKeys, setRegeneratingRasterKeys] = useState<Set<string>>(new Set());
    const [acceptingTextOnlyKeys, setAcceptingTextOnlyKeys] = useState<Set<string>>(new Set());
    const [uploadingRasterKeys, setUploadingRasterKeys] = useState<Set<string>>(new Set());
    const [checkingRasterKeys, setCheckingRasterKeys] = useState<Set<string>>(new Set());
    const [stillGeneratingAfterCheckKeys, setStillGeneratingAfterCheckKeys] = useState<Set<string>>(
        new Set()
    );
    const manualCheckPendingKeysRef = useRef<Set<string>>(new Set());
    const lastRasterStatusCheckAtRef = useRef<Record<string, number>>({});
    const checkStatusTimeoutHandlesRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const approvingRasterKeysRef = useRef(approvingRasterKeys);
    const [approvalBlockedUnitId, setApprovalBlockedUnitId] = useState<string | null>(null);
    const [approvalBlockedReason, setApprovalBlockedReason] = useState<UnitApprovalBlockedReason | null>(null);

    useEffect(() => {
        approvingVideoKeysRef.current = approvingVideoKeys;
    }, [approvingVideoKeys]);

    useEffect(() => {
        loadingStatesRef.current = loadingStates;
    }, [loadingStates]);

    useEffect(() => {
        approvingRasterKeysRef.current = approvingRasterKeys;
    }, [approvingRasterKeys]);

    useEffect(() => {
        regeneratingUnitIdRef.current = regeneratingUnitId;
    }, [regeneratingUnitId]);

    useEffect(() => {
        regeneratingLessonKeyRef.current = regeneratingLessonKey;
    }, [regeneratingLessonKey]);

    const beginRegeneratingUnit = useCallback((unitId: string) => {
        regeneratingUnitIdRef.current = unitId;
        setRegeneratingUnitId(unitId);
    }, []);

    const endRegeneratingUnit = useCallback(() => {
        regeneratingUnitIdRef.current = null;
        setRegeneratingUnitId(null);
    }, []);

    const beginRegeneratingLesson = useCallback((unitId: string, lessonId: string) => {
        const key = `${unitId}-${lessonId}`;
        regeneratingLessonKeyRef.current = key;
        setRegeneratingLessonKey(key);
    }, []);

    const endRegeneratingLesson = useCallback(() => {
        regeneratingLessonKeyRef.current = null;
        setRegeneratingLessonKey(null);
    }, []);

    const beginRegeneratingMaterial = useCallback((materialId: string) => {
        regeneratingMaterialKeyRef.current = materialId;
        setRegeneratingMaterialKey(materialId);
    }, []);

    const endRegeneratingMaterial = useCallback(() => {
        regeneratingMaterialKeyRef.current = null;
        setRegeneratingMaterialKey(null);
    }, []);

    const applyLessonRemovalToPanelState = useCallback((unitId: string, lessonId: string) => {
        const expandKey = lessonExpandKey(unitId, lessonId);
        const videoPrefix = lessonVideoStatePrefix(unitId, lessonId);

        setLocalUnits(prev => applyLessonRemovedFromUnits(prev as UnitDto[], unitId, lessonId) as UnitDto[]);
        setRemoveLessonLoadingKeys(prev => {
            const next = new Set(prev);
            next.delete(`${unitId}-${lessonId}`);
            return next;
        });
        setExpandedLessons(prev => {
            const next = new Set(prev);
            next.delete(expandKey);
            return next;
        });
        setRemovingLesson(prev =>
            prev?.lessonId === String(lessonId) && prev?.unitId === String(unitId) ? null : prev
        );
        setVideoSelectionOverrides(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (key.startsWith(videoPrefix)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setRasterApprovedOverrides(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (key.startsWith(videoPrefix)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setApprovingRasterKeys(prev => {
            const next = new Set(prev);
            let changed = false;
            for (const key of prev) {
                if (key.startsWith(videoPrefix)) {
                    next.delete(key);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setVideoApprovedForVideoId(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (key.startsWith(videoPrefix)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setApprovingVideoKeys(prev => {
            const next = new Set(prev);
            let changed = false;
            for (const key of prev) {
                if (key.startsWith(videoPrefix)) {
                    next.delete(key);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, []);

    const waitForLessonVideoSelectionsSave = useCallback((unitId: string, lessonId: string) => {
        const key = lessonVideoSaveWaiterKey(unitId, lessonId);
        return new Promise<void>(resolve => {
            lessonVideoSaveWaitersRef.current.set(key, resolve);
            window.setTimeout(() => {
                if (lessonVideoSaveWaitersRef.current.get(key) === resolve) {
                    lessonVideoSaveWaitersRef.current.delete(key);
                    resolve();
                }
            }, 8000);
        });
    }, []);

    const waitForUnitMediaApprovalsToSettle = useCallback(async (unitId: string) => {
        const prefix = `${normalizeIdPart(unitId)}|`;
        const deadline = Date.now() + 8000;
        while (Date.now() < deadline) {
            let hasPending = false;
            for (const key of approvingVideoKeysRef.current) {
                if (key.startsWith(prefix)) {
                    hasPending = true;
                    break;
                }
            }
            if (!hasPending) {
                for (const key of approvingRasterKeysRef.current) {
                    if (key.startsWith(prefix)) {
                        hasPending = true;
                        break;
                    }
                }
            }
            if (!hasPending) return;
            await sleep(50);
        }
    }, []);

    const queueSaveVideoSelections = useCallback(
        (unitId: string, lessonId: string, lesson: LessonDetailDto, overrides: Record<string, string | null>) => {
            const timerKey = lessonVideoSaveWaiterKey(unitId, lessonId);
            const existing = videoSaveTimersRef.current[timerKey];
            if (existing) window.clearTimeout(existing);

            videoSaveTimersRef.current[timerKey] = window.setTimeout(() => {
                const selections = buildVideoSelectionsForLesson(lesson, unitId, lessonId, overrides);
                if (!selections.length) return;
                void sendSaveVideoSelectionsCommand({
                    C322_UnitId: unitId,
                    C322_LessonId: lessonId,
                    C322_Selections: selections
                });
            }, 400);
        },
        [sendSaveVideoSelectionsCommand]
    );

    const handleUploadRaster = useCallback(
        (unitId: string, lessonId: string, stepId: string | undefined, figureKey: string | undefined, blobId: string, title?: string) => {
            if (!stepId || !figureKey || !blobId) return;
            const uploadKey = rasterApprovalStateKey(unitId, lessonId, stepId, figureKey);
            setUploadingRasterKeys(prev => new Set(prev).add(uploadKey));
            setRasterApprovedOverrides(prev => {
                const next = { ...prev };
                delete next[uploadKey];
                return next;
            });
            void sendUploadRasterFigureCommand({
                C436_UnitId: unitId,
                C436_LessonId: lessonId,
                C436_MaterialId: resolveStepIdForMediaCommand(stepId) ?? stepId,
                C436_FigureKey: figureKey,
                C436_BlobId: blobId,
                C436_Title: title,
            });
        },
        [sendUploadRasterFigureCommand]
    );

    const handleVideoSelectionChange = useCallback(
        (
            unitId: string,
            lessonId: string,
            lesson: LessonDetailDto,
            materialId: string | undefined,
            videoId: string | null
        ) => {
            if (!materialId) return;
            const material = findLessonMaterialById(lesson, materialId);
            if (!material) return;

            const currentId = getSelectedVideoId(material, unitId, lessonId, videoSelectionOverrides);
            if (currentId === videoId) return;

            const selectionKey = videoSelectionStateKey(unitId, lessonId, materialId);
            const approvalKey = videoApprovedOverrideKey(unitId, lessonId, materialId);
            const stepId = resolveStepIdForMediaCommand(materialId);
            const nextOverrides = { ...videoSelectionOverrides, [selectionKey]: videoId };

            setVideoSelectionOverrides(nextOverrides);
            queueSaveVideoSelections(unitId, lessonId, lesson, nextOverrides);
            setVideoApprovedForVideoId(prev => {
                const next = { ...prev };
                delete next[approvalKey];
                return next;
            });
            setApprovingVideoKeys(prev => {
                const next = new Set(prev);
                next.delete(approvalKey);
                return next;
            });
            if (stepId) {
                setLocalUnits(prev =>
                    applyLessonVideoSelectionChangedToUnits(prev, unitId, lessonId, stepId, videoId)
                );
            }
        },
        [queueSaveVideoSelections, videoSelectionOverrides]
    );

    const isVideoApproving = useCallback(
        (unitId: string, lessonId: string, material: SupportingMaterialDto | undefined) => {
            if (!material?.e251_MaterialId) return false;
            return approvingVideoKeys.has(
                videoApprovedOverrideKey(unitId, lessonId, material.e251_MaterialId)
            );
        },
        [approvingVideoKeys]
    );

    const isRasterApproving = useCallback(
        (unitId: string, lessonId: string, stepId: string | undefined, figureKey: string | undefined) => {
            if (!stepId || !figureKey) return false;
            return approvingRasterKeys.has(rasterApprovalStateKey(unitId, lessonId, stepId, figureKey));
        },
        [approvingRasterKeys]
    );

    const pendingMediaApprovalReason = useCallback(
        (unitId: string): PendingMediaApprovalReason | null => {
            const prefix = `${normalizeIdPart(unitId)}|`;
            let videoPending = false;
            let imagePending = false;
            for (const key of approvingVideoKeys) {
                if (key.startsWith(prefix)) {
                    videoPending = true;
                    break;
                }
            }
            for (const key of approvingRasterKeys) {
                if (key.startsWith(prefix)) {
                    imagePending = true;
                    break;
                }
            }
            if (videoPending && imagePending) return 'pending-media';
            if (videoPending) return 'pending-video';
            if (imagePending) return 'pending-image';
            return null;
        },
        [approvingVideoKeys, approvingRasterKeys]
    );

    const isVideoApproved = useCallback(
        (unitId: string, lessonId: string, material: SupportingMaterialDto | undefined) => {
            if (!material?.e251_MaterialId) return true;
            const key = videoApprovedOverrideKey(unitId, lessonId, material.e251_MaterialId);
            const selectedId = getSelectedVideoId(material, unitId, lessonId, videoSelectionOverrides);
            return isVideoApprovedForCurrentSelection(
                material,
                selectedId,
                videoApprovedForVideoId,
                key
            );
        },
        [videoApprovedForVideoId, videoSelectionOverrides]
    );

    const isRasterApprovedForFigure = useCallback(
        (
            unitId: string,
            lessonId: string,
            stepId: string | undefined,
            figure: { e251_FigureKey?: string; e251_RasterApproved?: boolean }
        ) => {
            if (!stepId || !figure.e251_FigureKey) return true;
            return isRasterFigureApproved(figure, unitId, lessonId, stepId, rasterApprovedOverrides);
        },
        [rasterApprovedOverrides]
    );

    const findFirstUnapprovedMedia = useCallback(
        (unit: UnitDto, treatApproved?: MediaReviewSlotRef): MediaReviewSlotRef | null =>
            findFirstUnapprovedMediaSlot(unit, {
                isVideoApproved: (lessonId, material) =>
                    isVideoApproved(unit.e251_UnitId || '', lessonId, material),
                isRasterApproved: (lessonId, stepId, figure) =>
                    isRasterApprovedForFigure(unit.e251_UnitId || '', lessonId, stepId, figure),
                treatApproved,
            }),
        [isRasterApprovedForFigure, isVideoApproved]
    );

    const scrollToMediaSlot = useCallback((unitId: string, slot: MediaReviewSlotRef) => {
        scheduleScrollElementIntoView(
            () =>
                document.getElementById(
                    mediaSlotDomId(
                        unitId,
                        slot.lessonId,
                        slot.materialId,
                        slot.kind,
                        slot.figureKey
                    )
                ),
            { delayMs: 220, block: 'center', force: true }
        );
    }, []);

    const scrollToFailedVectorDiagram = useCallback(
        (unitId: string, ref: { lessonId: string; stepId: string; figureKey: string }) => {
            scheduleScrollElementIntoView(
                () =>
                    document.getElementById(
                        vectorDiagramDomId(unitId, ref.lessonId, ref.stepId, ref.figureKey)
                    ),
                { delayMs: 220, block: 'center', force: true }
            );
        },
        []
    );

    const advanceAfterMediaApproval = useCallback(
        (unitId: string, approvedSlot: MediaReviewSlotRef) => {
            const unit = localUnits.find(u => u.e251_UnitId === unitId);
            if (!unit) return;

            const nextUnapproved = findFirstUnapprovedMedia(unit, approvedSlot);
            if (!nextUnapproved) {
                scheduleScrollUnitCardIntoView(unitId, { delayMs: 220, block: 'start', force: true });
                return;
            }
            if (
                nextUnapproved.lessonId === approvedSlot.lessonId &&
                nextUnapproved.materialId === approvedSlot.materialId &&
                nextUnapproved.figureKey === approvedSlot.figureKey
            ) {
                return;
            }

            setExpandedLessons(prev => new Set(prev).add(lessonExpandKey(unitId, nextUnapproved.lessonId)));
            scrollToMediaSlot(unitId, nextUnapproved);
        },
        [findFirstUnapprovedMedia, localUnits, scrollToMediaSlot]
    );

    const handleApproveVideos = useCallback(
        async (unitId: string, lessonId: string, lesson: LessonDetailDto, materialId: string | undefined) => {
            if (!materialId) return;
            const stepId = resolveStepIdForMediaCommand(materialId);
            if (!stepId) return;

            const timerKey = lessonVideoSaveWaiterKey(unitId, lessonId);
            const existing = videoSaveTimersRef.current[timerKey];
            if (existing) {
                window.clearTimeout(existing);
                delete videoSaveTimersRef.current[timerKey];
            }

            const selections = buildVideoSelectionsForLesson(lesson, unitId, lessonId, videoSelectionOverrides);
            if (selections.length) {
                const savePromise = waitForLessonVideoSelectionsSave(unitId, lessonId);
                void sendSaveVideoSelectionsCommand({
                    C322_UnitId: unitId,
                    C322_LessonId: lessonId,
                    C322_Selections: selections
                });
                await savePromise;
            }

            await waitForUnitMediaApprovalsToSettle(unitId);

            const material = findLessonMaterialById(lesson, materialId);
            const selectedId = material
                ? getSelectedVideoId(material, unitId, lessonId, videoSelectionOverrides)
                : null;
            const approvalKey = videoApprovedOverrideKey(unitId, lessonId, materialId);
            setVideoApprovedForVideoId(prev => ({
                ...prev,
                [approvalKey]: selectedId ?? null
            }));
            setApprovingVideoKeys(prev => new Set(prev).add(approvalKey));

            void sendApproveLessonVideosCommand({
                C323_UnitId: unitId,
                C323_LessonId: lessonId,
                C323_MaterialId: stepId
            });

            setApprovalBlockedUnitId(prev => {
                if (prev === unitId) {
                    setApprovalBlockedReason(null);
                    return null;
                }
                return prev;
            });

            advanceAfterMediaApproval(unitId, {
                kind: 'video',
                lessonId,
                materialId,
            });
        },
        [
            advanceAfterMediaApproval,
            sendApproveLessonVideosCommand,
            sendSaveVideoSelectionsCommand,
            videoSelectionOverrides,
            waitForLessonVideoSelectionsSave,
            waitForUnitMediaApprovalsToSettle
        ]
    );

    const handleApproveRaster = useCallback(
        async (
            unitId: string,
            lessonId: string,
            stepId: string | undefined,
            figureKey: string | undefined
        ) => {
            if (!stepId || !figureKey) return;

            await waitForUnitMediaApprovalsToSettle(unitId);

            const approvalKey = rasterApprovalStateKey(unitId, lessonId, stepId, figureKey);
            setRasterApprovedOverrides(prev => ({
                ...prev,
                [approvalKey]: true
            }));
            setApprovingRasterKeys(prev => new Set(prev).add(approvalKey));

            void sendApproveLessonRasterFigureCommand({
                C336_UnitId: unitId,
                C336_LessonId: lessonId,
                C336_MaterialId: resolveStepIdForMediaCommand(stepId) ?? stepId,
                C336_FigureKey: figureKey
            });

            setApprovalBlockedUnitId(prev => {
                if (prev === unitId) {
                    setApprovalBlockedReason(null);
                    return null;
                }
                return prev;
            });

            advanceAfterMediaApproval(unitId, {
                kind: 'raster',
                lessonId,
                materialId: stepId,
                figureKey,
            });
        },
        [
            advanceAfterMediaApproval,
            sendApproveLessonRasterFigureCommand,
            waitForUnitMediaApprovalsToSettle
        ]
    );

    const handleRegenerateRaster = useCallback(
        (
            unitId: string,
            lessonId: string,
            stepId: string | undefined,
            figureKey: string | undefined,
            revisionNotes?: string
        ) => {
            if (!stepId || !figureKey) return;
            const regenKey = rasterApprovalStateKey(unitId, lessonId, stepId, figureKey);
            setRegeneratingRasterKeys(prev => new Set(prev).add(regenKey));
            setRasterApprovedOverrides(prev => {
                const next = { ...prev };
                delete next[regenKey];
                return next;
            });
            setLocalUnits(prev =>
                applyLessonRasterFigureReadyToUnits(
                    prev as UnitDto[],
                    unitId,
                    lessonId,
                    stepId,
                    figureKey,
                    {
                        e251_GenerationState: 'generating',
                        e251_GenerationStartedAt: new Date().toISOString(),
                        e251_ImageUrl: undefined,
                        e251_RasterTitle: undefined,
                        e251_GenerationError: undefined,
                        e251_RasterApproved: false,
                    }
                ) as UnitDto[]
            );
            void sendRegenerateLessonFigureCommand({
                C333_UnitId: unitId,
                C333_LessonId: lessonId,
                C333_MaterialId: resolveStepIdForMediaCommand(stepId) ?? stepId,
                C333_FigureKey: figureKey,
                ...(revisionNotes ? { C333_RevisionNotes: revisionNotes } : {}),
            });
        },
        [sendRegenerateLessonFigureCommand]
    );

    const isRasterRegenerating = useCallback(
        (unitId: string, lessonId: string, stepId: string | undefined, figureKey: string | undefined) => {
            if (!stepId || !figureKey) return false;
            return regeneratingRasterKeys.has(
                rasterApprovalStateKey(unitId, lessonId, stepId, figureKey)
            );
        },
        [regeneratingRasterKeys]
    );

    const handleAcceptTextOnly = useCallback(
        (
            unitId: string,
            lessonId: string,
            stepId: string | undefined,
            figureKey: string | undefined
        ) => {
            if (!stepId || !figureKey) return;
            const acceptKey = rasterApprovalStateKey(unitId, lessonId, stepId, figureKey);
            setAcceptingTextOnlyKeys(prev => new Set(prev).add(acceptKey));
            void sendAcceptLessonFigureTextOnlyCommand({
                C442_UnitId: unitId,
                C442_LessonId: lessonId,
                C442_MaterialId: resolveStepIdForMediaCommand(stepId) ?? stepId,
                C442_FigureKey: figureKey,
            });
        },
        [sendAcceptLessonFigureTextOnlyCommand]
    );

    const isAcceptingTextOnly = useCallback(
        (unitId: string, lessonId: string, stepId: string | undefined, figureKey: string | undefined) => {
            if (!stepId || !figureKey) return false;
            return acceptingTextOnlyKeys.has(
                rasterApprovalStateKey(unitId, lessonId, stepId, figureKey)
            );
        },
        [acceptingTextOnlyKeys]
    );

    const clearRasterStatusCheckState = useCallback((key: string) => {
        manualCheckPendingKeysRef.current.delete(key);
        setCheckingRasterKeys(prev => {
            if (!prev.has(key)) return prev;
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        const timeoutHandle = checkStatusTimeoutHandlesRef.current[key];
        if (timeoutHandle) {
            window.clearTimeout(timeoutHandle);
            delete checkStatusTimeoutHandlesRef.current[key];
        }
    }, []);

    const handleCheckRasterStatus = useCallback(
        (
            unitId: string,
            lessonId: string,
            stepId: string | undefined,
            figureKey: string | undefined
        ) => {
            if (!stepId || !figureKey) return;
            const key = rasterApprovalStateKey(unitId, lessonId, stepId, figureKey);
            const now = Date.now();
            const lastCheckAt = lastRasterStatusCheckAtRef.current[key] ?? 0;
            if (now - lastCheckAt < 2000) return;
            lastRasterStatusCheckAtRef.current[key] = now;

            manualCheckPendingKeysRef.current.add(key);
            setCheckingRasterKeys(prev => new Set(prev).add(key));
            setStillGeneratingAfterCheckKeys(prev => {
                if (!prev.has(key)) return prev;
                const next = new Set(prev);
                next.delete(key);
                return next;
            });

            const existingTimeout = checkStatusTimeoutHandlesRef.current[key];
            if (existingTimeout) window.clearTimeout(existingTimeout);
            checkStatusTimeoutHandlesRef.current[key] = window.setTimeout(() => {
                clearRasterStatusCheckState(key);
            }, 30_000);

            sendGetLessonRasterFigureCommand({
                C438_UnitId: unitId,
                C438_LessonId: lessonId,
                C438_StepId: resolveStepIdForMediaCommand(stepId) ?? stepId,
                C438_FigureKey: figureKey,
            });
        },
        [clearRasterStatusCheckState, sendGetLessonRasterFigureCommand]
    );

    const isRasterStatusChecking = useCallback(
        (unitId: string, lessonId: string, stepId: string | undefined, figureKey: string | undefined) => {
            if (!stepId || !figureKey) return false;
            return checkingRasterKeys.has(
                rasterApprovalStateKey(unitId, lessonId, stepId, figureKey)
            );
        },
        [checkingRasterKeys]
    );

    const isRasterStillGeneratingAfterCheck = useCallback(
        (unitId: string, lessonId: string, stepId: string | undefined, figureKey: string | undefined) => {
            if (!stepId || !figureKey) return false;
            return stillGeneratingAfterCheckKeys.has(
                rasterApprovalStateKey(unitId, lessonId, stepId, figureKey)
            );
        },
        [stillGeneratingAfterCheckKeys]
    );

    const attemptApproveUnit = useCallback(
        (unit: UnitDto) => {
            const unitId = unit.e251_UnitId || '';
            const pendingReason = pendingMediaApprovalReason(unitId);
            if (pendingReason) {
                setApprovalBlockedUnitId(unitId);
                setApprovalBlockedReason(pendingReason);
                return;
            }

            const firstUnapproved = findFirstUnapprovedMedia(unit);
            if (firstUnapproved) {
                setApprovalBlockedUnitId(unitId);
                setApprovalBlockedReason(firstUnapproved.reason);
                setExpandedUnits(new Set([unitId]));
                setExpandedLessons(prev => new Set(prev).add(lessonExpandKey(unitId, firstUnapproved.lessonId)));
                scrollToMediaSlot(unitId, firstUnapproved);
                return;
            }

            const failedDiagram = findFirstFailedVectorDiagram(unit);
            if (failedDiagram) {
                setApprovalBlockedUnitId(unitId);
                setApprovalBlockedReason('diagram-failed');
                setExpandedUnits(new Set([unitId]));
                setExpandedLessons(prev =>
                    new Set(prev).add(lessonExpandKey(unitId, failedDiagram.lessonId))
                );
                scrollToFailedVectorDiagram(unitId, failedDiagram);
                return;
            }

            setApprovalBlockedUnitId(prev => {
                if (prev === unitId) {
                    setApprovalBlockedReason(null);
                    return null;
                }
                return prev;
            });
            setLoadingStates(prev => new Set(prev).add(unitId));
            onApproveUnit(unitId);
        },
        [findFirstUnapprovedMedia, onApproveUnit, pendingMediaApprovalReason, scrollToFailedVectorDiagram, scrollToMediaSlot]
    );

    const toggleUnit = (unitId: string) => {
        const wasExpanded = expandedUnits.has(unitId);
        if (wasExpanded) {
            setExpandedUnits(new Set());
            return;
        }
        if (!touchedUnits.has(unitId) && onMarkUnitTouched) {
            const unit = localUnits.find(u => u.e251_UnitId === unitId);
            if (unit && !unit.e251_Touched) {
                setTouchedUnits(prevTouch => new Set(prevTouch).add(unitId));
                onMarkUnitTouched(unitId);
            }
        }
        setExpandedUnits(new Set([unitId]));
        scheduleScrollUnitCardIntoView(unitId);
    };

    const toggleLesson = (unitId: string, lessonId: string) => {
        const key = lessonExpandKey(unitId, lessonId);
        setExpandedLessons(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    // Sync local units from props - intelligently merge to preserve object references
    useEffect(() => {
        if (localUnits.length === 0) {
            // Initial load - set all units
            setLocalUnits(units);
        } else {
            // Intelligently merge - keep unchanged unit references to prevent re-renders
            setLocalUnits(prevUnits => {
                // Create a map of previous units by ID for quick lookup
                const prevUnitsMap = new Map(prevUnits.map(u => [u.e251_UnitId, u]));
                const newUnitIds = new Set(units.map(u => u.e251_UnitId));
                
                // Build new array, preserving references where possible
                const mergedUnits = units.map(newUnit => {
                    const prevUnit = prevUnitsMap.get(newUnit.e251_UnitId);
                    
                    if (!prevUnit) {
                        // New unit - use as is
                        return newUnit;
                    }

                    if (lessonIdsSignature(prevUnit.e251_Lessons) !== lessonIdsSignature(newUnit.e251_Lessons)) {
                        return newUnit;
                    }
                    
                    // Check if unit changed (compare stringified versions)
                    const prevJson = JSON.stringify(prevUnit);
                    const newJson = JSON.stringify(newUnit);
                    
                    if (prevJson === newJson) {
                        // No change - preserve reference to prevent re-render
                        return prevUnit;
                    }
                    
                    // Unit changed - use new version
                    return newUnit;
                });
                
                // Check if any units were removed
                const removedUnits = prevUnits.filter(u => !newUnitIds.has(u.e251_UnitId));
                
                // If nothing changed at all, return previous reference
                if (mergedUnits.every((u, i) => u === prevUnits[i]) && 
                    mergedUnits.length === prevUnits.length && 
                    removedUnits.length === 0) {
                    return prevUnits;
                }
                
                return mergedUnits;
            });
        }
    }, [units, localUnits.length]);

    useEffect(() => {
        if (!lessonUnitFocus?.unitId || !onConsumedLessonUnitFocus) return;
        const unitId = lessonUnitFocus.unitId;
        const appears =
            localUnits.some(u => u.e251_UnitId === unitId) || units.some(u => u.e251_UnitId === unitId);
        if (!appears) return;

        setExpandedUnits(new Set([unitId]));

        const handle = scheduleScrollUnitCardIntoView(unitId, { delayMs: 0 });
        const consumeHandle = window.setTimeout(() => {
            onConsumedLessonUnitFocus();
        }, 0);
        return () => {
            window.clearTimeout(handle);
            window.clearTimeout(consumeHandle);
        };
    }, [lessonUnitFocus, localUnits, units, onConsumedLessonUnitFocus]);

    // Event listeners for state updates - only clear loading states
    // Parent component (LessonsPage) already handles the data refresh
    useEvent({
        eventName: 'E254v1_UnitApproved',
        onEventReceived: (event: any) => {
            const key = event.e254_UnitId;
            setLoadingStates(prev => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    });

    useEvent({
        eventName: 'E314v1_LessonVideoSelectionsSaved',
        onEventReceived: (event: any) => {
            const unitId = event?.e314_UnitId ?? event?.E314_UnitId;
            const lessonId = event?.e314_LessonId ?? event?.E314_LessonId;
            if (!unitId || !lessonId) return;
            const key = lessonVideoSaveWaiterKey(unitId, lessonId);
            const resolve = lessonVideoSaveWaitersRef.current.get(key);
            if (resolve) {
                lessonVideoSaveWaitersRef.current.delete(key);
                resolve();
            }
        }
    });

    useEvent({
        eventName: 'E315v1_LessonVideosApproved',
        onEventReceived: (event: any) => {
            const unitId = event?.e315_UnitId ?? event?.E315_UnitId;
            const lessonId = event?.e315_LessonId ?? event?.E315_LessonId;
            const materialId = event?.e315_MaterialId ?? event?.E315_MaterialId;
            if (!unitId || !lessonId || !materialId) return;
            const approvalKey = videoApprovedOverrideKey(String(unitId), String(lessonId), String(materialId));
            const stepId = normalizeIdPart(
                resolveStepIdForMediaCommand(String(materialId)) ?? materialId
            );
            const prefix = `${normalizeIdPart(unitId)}|${normalizeIdPart(lessonId)}|`;
            setApprovingVideoKeys(prev => {
                const next = new Set(prev);
                next.delete(approvalKey);
                // Virtual material ids (`{stepId}:30`) were used as keys before canonicalization.
                next.delete(`${prefix}${stepId}:30`);
                return next;
            });
        }
    });

    useEvent({
        eventName: 'E255v1_UnitDeleted',
        onEventReceived: (event: any) => {
            const key = event.e255_UnitId;
            setLoadingStates(prev => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    });

    useEvent({
        eventName: 'E256v1_UnitRegenerated',
        onEventReceived: async (event: any) => {
            const key = event.e256_UnitId;
            setLoadingStates(prev => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
            if (runUnitFinishingStepRef.current) {
                await runUnitFinishingStepRef.current();
            }
            endRegeneratingUnit();
            if (key) {
                const prefix = `${normalizeIdPart(key)}|`;
                setVideoApprovedForVideoId(prev => {
                    const next = { ...prev };
                    for (const approvalKey of Object.keys(next)) {
                        if (approvalKey.startsWith(prefix)) {
                            delete next[approvalKey];
                        }
                    }
                    return next;
                });
                setApprovingVideoKeys(prev => {
                    const next = new Set(prev);
                    for (const approvalKey of prev) {
                        if (approvalKey.startsWith(prefix)) {
                            next.delete(approvalKey);
                        }
                    }
                    return next;
                });
                setVideoSelectionOverrides(prev => {
                    const next = { ...prev };
                    for (const selectionKey of Object.keys(next)) {
                        if (selectionKey.startsWith(prefix)) {
                            delete next[selectionKey];
                        }
                    }
                    return next;
                });
                setRasterApprovedOverrides(prev => {
                    const next = { ...prev };
                    for (const approvalKey of Object.keys(next)) {
                        if (approvalKey.startsWith(prefix)) {
                            delete next[approvalKey];
                        }
                    }
                    return next;
                });
                setRasterSelectionOverrides(prev => {
                    const next = { ...prev };
                    for (const selectionKey of Object.keys(next)) {
                        if (selectionKey.startsWith(prefix)) {
                            delete next[selectionKey];
                        }
                    }
                    return next;
                });
                setApprovingRasterKeys(prev => {
                    const next = new Set(prev);
                    for (const approvalKey of prev) {
                        if (approvalKey.startsWith(prefix)) {
                            next.delete(approvalKey);
                        }
                    }
                    return next;
                });
            }
        }
    });

    const clearLessonMediaReviewOverrides = useCallback((unitId: string, lessonId: string) => {
        const videoPrefix = lessonVideoStatePrefix(String(unitId), String(lessonId));
        setVideoSelectionOverrides(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (key.startsWith(videoPrefix)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setRasterSelectionOverrides(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (key.startsWith(videoPrefix)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setVideoApprovedForVideoId(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (key.startsWith(videoPrefix)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setRasterApprovedOverrides(prev => {
            const next = { ...prev };
            let changed = false;
            for (const key of Object.keys(next)) {
                if (key.startsWith(videoPrefix)) {
                    delete next[key];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
        setApprovingVideoKeys(prev => {
            const next = new Set(prev);
            for (const key of prev) {
                if (key.startsWith(videoPrefix)) {
                    next.delete(key);
                }
            }
            return next;
        });
        setApprovingRasterKeys(prev => {
            const next = new Set(prev);
            for (const key of prev) {
                if (key.startsWith(videoPrefix)) {
                    next.delete(key);
                }
            }
            return next;
        });
    }, []);

    useEvent({
        eventName: 'E334v1_LessonRegenerated',
        onEventReceived: async (event: any) => {
            if (runLessonFinishingStepRef.current) {
                await runLessonFinishingStepRef.current();
            }
            endRegeneratingLesson();

            const unitId = event?.e334_UnitId ?? event?.E334_UnitId;
            const previousLessonId = event?.e334_PreviousLessonId ?? event?.E334_PreviousLessonId;
            if (!unitId || !previousLessonId) return;

            clearLessonMediaReviewOverrides(String(unitId), String(previousLessonId));
        }
    });

    useEvent({
        eventName: 'E439v1_LessonMaterialRegenerated',
        onEventReceived: async (event: any) => {
            if (runMaterialFinishingStepRef.current) {
                await runMaterialFinishingStepRef.current();
            }
            endRegeneratingMaterial();

            const unitId = event?.e439_UnitId ?? event?.E439_UnitId;
            const lessonId = event?.e439_LessonId ?? event?.E439_LessonId;
            if (!unitId || !lessonId) return;

            clearLessonMediaReviewOverrides(String(unitId), String(lessonId));
        }
    });

    useEvent({
        eventName: 'E435v1_LessonRasterFigureReady',
        onEventReceived: (event: any) => {
            const unitId = event?.e435_UnitId ?? event?.E435_UnitId;
            const lessonId = event?.e435_LessonId ?? event?.E435_LessonId;
            const stepId = event?.e435_StepId ?? event?.E435_StepId;
            const figureKey = event?.e435_FigureKey ?? event?.E435_FigureKey;
            if (!unitId || !lessonId || !stepId || !figureKey) return;

            const regenKey = rasterApprovalStateKey(
                String(unitId),
                String(lessonId),
                String(stepId),
                String(figureKey)
            );
            const generationState = event?.e435_GenerationState ?? event?.E435_GenerationState;
            const normalizedGenerationState = String(generationState ?? '').trim().toLowerCase();
            const wasManualCheck = manualCheckPendingKeysRef.current.has(regenKey);

            if (normalizedGenerationState === 'generating') {
                if (wasManualCheck) {
                    clearRasterStatusCheckState(regenKey);
                    setStillGeneratingAfterCheckKeys(prev => new Set(prev).add(regenKey));
                }
                return;
            }

            clearRasterStatusCheckState(regenKey);
            setStillGeneratingAfterCheckKeys(prev => {
                if (!prev.has(regenKey)) return prev;
                const next = new Set(prev);
                next.delete(regenKey);
                return next;
            });
            setRegeneratingRasterKeys(prev => {
                const next = new Set(prev);
                next.delete(regenKey);
                return next;
            });
            setUploadingRasterKeys(prev => {
                const next = new Set(prev);
                next.delete(regenKey);
                return next;
            });
            const imageUrl = event?.e435_ImageUrl ?? event?.E435_ImageUrl;
            const rasterTitle = event?.e435_RasterTitle ?? event?.E435_RasterTitle;
            const generationError = event?.e435_GenerationError ?? event?.E435_GenerationError;
            const rasterApproved = event?.e435_RasterApproved ?? event?.E435_RasterApproved ?? false;
            const selectedOptionId = event?.e435_SelectedOptionId ?? event?.E435_SelectedOptionId;
            const imageGenerationPrompt =
                event?.e435_ImageGenerationPrompt ?? event?.E435_ImageGenerationPrompt;
            const rasterOptions = event?.e435_RasterOptions ?? event?.E435_RasterOptions;

            setLocalUnits(prev =>
                applyLessonRasterFigureReadyToUnits(
                    prev as UnitDto[],
                    unitId,
                    lessonId,
                    stepId,
                    figureKey,
                    {
                        e251_GenerationState: generationState,
                        e251_ImageUrl: imageUrl,
                        e251_RasterTitle: rasterTitle,
                        e251_GenerationError: generationError,
                        e251_RasterApproved: rasterApproved,
                        e251_SelectedOptionId: selectedOptionId,
                        ...(typeof imageGenerationPrompt === 'string' &&
                        imageGenerationPrompt.trim()
                            ? { e251_ImageGenerationPrompt: imageGenerationPrompt }
                            : {}),
                        e251_RasterOptions: mapEventRasterOptions(rasterOptions),
                    }
                ) as UnitDto[]
            );
        }
    });

    useEvent({
        eventName: 'E337v1_LessonRasterFigureApproved',
        onEventReceived: (event: any) => {
            const unitId = event?.e337_UnitId ?? event?.E337_UnitId;
            const lessonId = event?.e337_LessonId ?? event?.E337_LessonId;
            const materialId = event?.e337_MaterialId ?? event?.E337_MaterialId;
            const figureKey = event?.e337_FigureKey ?? event?.E337_FigureKey;
            if (!unitId || !lessonId || !materialId || !figureKey) return;
            const approvalKey = rasterApprovalStateKey(
                String(unitId),
                String(lessonId),
                String(materialId),
                String(figureKey)
            );
            setLocalUnits(prev =>
                applyLessonRasterFigureApprovedToUnits(
                    prev as UnitDto[],
                    unitId,
                    lessonId,
                    materialId,
                    figureKey
                ) as UnitDto[]
            );
            setApprovingRasterKeys(prev => {
                const next = new Set(prev);
                next.delete(approvalKey);
                return next;
            });
            setRasterApprovedOverrides(prev => ({
                ...prev,
                [approvalKey]: true
            }));
        }
    });

    useEvent({
        eventName: 'E441v1_LessonFigureTextOnlyAccepted',
        onEventReceived: (event: any) => {
            const unitId = event?.e441_UnitId ?? event?.E441_UnitId;
            const lessonId = event?.e441_LessonId ?? event?.E441_LessonId;
            const materialId = event?.e441_MaterialId ?? event?.E441_MaterialId;
            const figureKey = event?.e441_FigureKey ?? event?.E441_FigureKey;
            const materialContent = event?.e441_MaterialContent ?? event?.E441_MaterialContent;
            const isRaster = event?.e441_IsRaster ?? event?.E441_IsRaster;
            if (!unitId || !lessonId || !materialId || !figureKey) return;
            const acceptKey = rasterApprovalStateKey(
                String(unitId),
                String(lessonId),
                String(materialId),
                String(figureKey)
            );
            setLocalUnits(prev =>
                applyLessonFigureTextOnlyAcceptedToUnits(
                    prev as UnitDto[],
                    unitId,
                    lessonId,
                    materialId,
                    figureKey,
                    materialContent,
                    isRaster
                ) as UnitDto[]
            );
            setAcceptingTextOnlyKeys(prev => {
                const next = new Set(prev);
                next.delete(acceptKey);
                return next;
            });
            setRasterApprovedOverrides(prev => ({
                ...prev,
                [acceptKey]: true
            }));
        }
    });

    // Listen for errors - if regeneration fails, close the loading dialog
    useApiErrors(() => {
        if (regeneratingUnitIdRef.current) {
            endRegeneratingUnit();
        }
        if (regeneratingLessonKeyRef.current) {
            endRegeneratingLesson();
        }
        const pendingVideoKeys = approvingVideoKeysRef.current;
        if (pendingVideoKeys.size) {
            setVideoApprovedForVideoId(prev => {
                const next = { ...prev };
                for (const key of pendingVideoKeys) delete next[key];
                return next;
            });
        }
        const pendingRasterKeys = approvingRasterKeysRef.current;
        if (pendingRasterKeys.size) {
            setRasterApprovedOverrides(prev => {
                const next = { ...prev };
                for (const key of pendingRasterKeys) delete next[key];
                return next;
            });
        }
        setApprovingVideoKeys(new Set());
        setApprovingRasterKeys(new Set());
        setRegeneratingRasterKeys(new Set());
        setRemoveLessonLoadingKeys(new Set());
        if (loadingStatesRef.current.size) {
            setLoadingStates(new Set());
        }
    });

    useEvent({
        eventName: 'E330v1_LessonRemovedFromUnit',
        onEventReceived: (event: any) => {
            const unitId = event?.e330_UnitId ?? event?.E330_UnitId;
            const lessonId = event?.e330_LessonId ?? event?.E330_LessonId;
            if (!unitId || !lessonId) return;

            applyLessonRemovalToPanelState(String(unitId), String(lessonId));
        }
    });

    // Listen for unit touched event to update local state (remove starburst)
    useEvent({
        eventName: 'E269v1_UnitTouched',
        onEventReceived: (event: any) => {
            if (event.e269_UnitId) {
                // Update touchedUnits Set
                setTouchedUnits(prev => new Set(prev).add(event.e269_UnitId));
                
                // Update local units state
                setLocalUnits(prevUnits => {
                    return prevUnits.map(unit => {
                        if (unit.e251_UnitId === event.e269_UnitId && unit.e251_Touched !== true) {
                            return { ...unit, e251_Touched: true };
                        }
                        return unit;
                    });
                });
            }
        }
    });

    const handlePrintLesson = (lesson: LessonDetailDto) => {
        const html = buildLessonPrintHtml({
            lesson: lesson as PrintableLessonDto,
            statusLabel: 'In Review',
            draft: true
        });
        void openLessonPrintWindow(html, idToken);
    };

    const handleRegenerateClick = (unitId: string, unitTitle: string) => {
        setRegeneratingUnit({ unitId, unitTitle });
    };

    const handleRegenerateComplete = (wasConfirmed: boolean, unitId: string) => {
        setRegeneratingUnit(null);
        // Show thinking animation only if user confirmed
        if (wasConfirmed) {
            beginRegeneratingUnit(unitId);
        }
        // Don't refresh here - if regeneration was successful, the event handler will refresh
        // If user canceled, no refresh is needed since nothing changed
    };

    const handleRegenerateLessonClick = (unitId: string, lessonId: string, lessonTitle: string) => {
        setRegeneratingLesson({ unitId, lessonId, lessonTitle });
    };

    const handleRegenerateLessonComplete = (wasConfirmed: boolean, unitId: string, lessonId: string) => {
        setRegeneratingLesson(null);
        if (wasConfirmed) {
            beginRegeneratingLesson(unitId, lessonId);
        }
    };

    const handleRegenerateMaterialClick = (
        unitId: string,
        lessonId: string,
        material: SupportingMaterialDto
    ) => {
        const materialType = material.e251_MaterialType;
        if (materialType !== '10' && materialType !== '20') return;
        const stepId = resolveStepIdForMediaCommand(material.e251_MaterialId);
        if (!stepId || !material.e251_MaterialId) return;
        setRegeneratingMaterial({
            unitId,
            lessonId,
            stepId,
            materialType,
            materialTitle: material.e251_Title || (materialType === '10' ? 'Worksheet' : 'Reading'),
            materialId: material.e251_MaterialId
        });
    };

    const handleRegenerateMaterialComplete = (wasConfirmed: boolean, materialId: string) => {
        setRegeneratingMaterial(null);
        if (wasConfirmed) {
            beginRegeneratingMaterial(materialId);
        }
    };

    const handleDeleteClick = (unitId: string, unitTitle: string) => {
        setDeletingUnit({ unitId, unitTitle });
    };

    const handleDeleteConfirm = (unitId: string) => {
        // Mark unit as loading when user confirms
        setLoadingStates(prev => new Set(prev).add(unitId));
    };

    const handleDeleteComplete = () => {
        setDeletingUnit(null);
        // Don't refresh here - if deletion was successful, the event handler will refresh
        // If user canceled, no refresh is needed since nothing changed
    };

    const handleRemoveLessonClick = (unitId: string, lessonId: string, lessonTitle: string) => {
        setRemovingLesson({ unitId, lessonId, lessonTitle });
    };

    const handleRemoveLessonConfirm = (unitId: string, lessonId: string) => {
        setRemoveLessonLoadingKeys(prev => new Set(prev).add(`${unitId}-${lessonId}`));
    };

    const handleRemoveLessonClose = () => {
        setRemovingLesson(null);
    };

    switch (viewState) {
        case viewStates.loading:
            // Show "No Students" message if there are no students and not loading students
            if (!isStudentLoading && students.length === 0) {
                return (
                    <PlanNoStudentsEmptyState description="You need to create a student before you can approve units." />
                );
            }
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                        <span className="text-gray-600">Loading units...</span>
                    </div>
                </div>
            );

        case viewStates.ready:
            if (localUnits.length === 0) {
                return (
                    <div className="text-center py-12">
                        <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No units to review</h3>
                        <p className="mt-1 text-sm text-gray-500">Create a unit to get started</p>
                    </div>
                );
            }

            return (
        <div className="space-y-4">
            {localUnits.map((unit) => {
                const unitId = unit.e251_UnitId || '';
                const isUnitExpanded = expandedUnits.has(unitId);
                const lessons = unit.e251_Lessons || [];
                const { topics, subjects, learningStyles, totalDuration, createdAt, createdByName } =
                    deriveUnitHeaderFields(unit);

                const isUnitLoading = loadingStates.has(unitId) || regeneratingUnitId === unitId;

                // Show Starburst based on Touched property (not state)
                const showStarburst = !unit.e251_Touched;
                const isPeerUnitDimmed = expandedUnits.size > 0 && !isUnitExpanded;
                const unitHeaderTone: UnitHeaderTone = 'default';

                const isReviewing = unit.e251_State === UnitStateKey.Reviewing;
                const effectiveTouched = unit.e251_Touched === true || touchedUnits.has(unitId);
                const showApproveInHeader = isReviewing && effectiveTouched;
                const showApproveInUnitBody = isReviewing && !effectiveTouched;
                const showReviewInHeader = !isUnitExpanded;
                const reviewHeaderButton = showReviewInHeader ? (
                    <Button
                        variant="default"
                        size="sm"
                        onClick={() => toggleUnit(unitId)}
                        className="bg-action-blue hover:bg-action-blue-dark text-white"
                    >
                        <Eye size={16} className="mr-2" />
                        Review
                    </Button>
                ) : null;

                return (
                    <Card
                        key={unitId}
                        id={unitId ? lessonsUnitElementId(unitId) : undefined}
                        className={cn(
                            unitCardShellClass,
                            'relative transition-[opacity,filter] duration-200 ease-out',
                            isUnitLoading && 'opacity-50 pointer-events-none',
                            isPeerUnitDimmed &&
                                !isUnitLoading &&
                                'opacity-[0.88] contrast-[0.97] saturate-[0.96]'
                        )}
                    >
                        {/* New Unit Badge */}
                        <UnitCardHeaderNewBadge visible={showStarburst} />
                        {/* Unit Header */}
                        <div
                            onClick={() => toggleUnit(unitId)}
                            className={unitCardHeaderClass}
                        >
                            {/* Mobile: Review to expand; Approve once the unit has been viewed */}
                            <div className="md:hidden mb-3" onClick={(e) => e.stopPropagation()}>
                                <div className="flex flex-col gap-2">
                                    {reviewHeaderButton}
                                    {showApproveInHeader && (
                                        <>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => attemptApproveUnit(unit)}
                                                disabled={loadingStates.has(unitId)}
                                                className={confirmActionOutlineButtonClass}
                                            >
                                                {loadingStates.has(unitId) ? (
                                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                                ) : (
                                                    <CheckCircle size={16} className="mr-2" />
                                                )}
                                                Approve
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDeleteClick(unitId, topics.join(', ') || 'Untitled Unit')}
                                                disabled={loadingStates.has(unitId)}
                                                className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                            >
                                                <Trash2 size={16} className={`mr-2 ${loadingStates.has(unitId) ? 'animate-spin' : ''}`} />
                                                Delete
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex min-w-0 flex-1 items-center">
                                        {isUnitExpanded ? (
                                            <ChevronDown size={20} className="mr-3 shrink-0 self-center text-gray-600" />
                                        ) : (
                                            <ChevronRight size={20} className="mr-3 shrink-0 self-center text-gray-600" />
                                        )}
                                        <div className="min-w-0 flex-1 flex flex-col gap-3">
                                            <h3 className="m-0 text-lg font-normal text-gray-900 md:hidden">
                                                <UnitHeaderSubjectAndTopics
                                                    subjects={subjects}
                                                    topics={topics}
                                                    tone={unitHeaderTone}
                                                    showTopicsLabel={false}
                                                    learningObjectives={unit.e251_LearningObjectives}
                                                />
                                            </h3>
                                            <h3 className="m-0 hidden text-lg font-normal text-gray-900 md:block">
                                                <UnitHeaderSubjectLine
                                                    subjects={subjects}
                                                    tone={unitHeaderTone}
                                                />
                                            </h3>
                                        </div>
                                    </div>
                                    <div
                                        className="hidden shrink-0 self-center md:flex md:flex-col lg:flex-row md:items-stretch lg:items-center gap-2 ml-4"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {reviewHeaderButton}
                                        {showApproveInHeader && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => attemptApproveUnit(unit)}
                                                    disabled={loadingStates.has(unitId)}
                                                    className={confirmActionOutlineButtonClass}
                                                >
                                                    {loadingStates.has(unitId) ? (
                                                        <Loader2 size={16} className="mr-2 animate-spin" />
                                                    ) : (
                                                        <CheckCircle size={16} className="mr-2" />
                                                    )}
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleDeleteClick(unitId, topics.join(', ') || 'Untitled Unit')}
                                                    disabled={loadingStates.has(unitId)}
                                                    className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                                >
                                                    <Trash2 size={16} className={`mr-2 ${loadingStates.has(unitId) ? 'animate-spin' : ''}`} />
                                                    Delete
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="hidden min-w-0 w-full flex-col gap-3 pl-8 md:flex">
                                    <UnitHeaderTopicsWithObjectives
                                        topics={topics}
                                        learningObjectives={unit.e251_LearningObjectives}
                                        tone={unitHeaderTone}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Unit Content */}
                        {isUnitExpanded && (
                            <div className={unitCardBodyClass}>
                                <div className="p-6">
                                    {(learningStyles.length > 0 || totalDuration > 0 || createdAt || createdByName) ? (
                                        <div className={unitCardBodyMetaClass}>
                                            {totalDuration > 0 && (
                                                <span className="flex items-center">
                                                    <Clock size={14} className="mr-1 text-gray-600" />
                                                    {formatTimeDisplay(totalDuration)}
                                                </span>
                                            )}
                                            {learningStyles.length > 0 && (
                                                <span className="flex items-center">
                                                    <GraduationCap size={14} className="mr-1 text-gray-600" />
                                                    {learningStyles.join(', ')}
                                                </span>
                                            )}
                                            <UnitCardCreatedByMeta
                                                createdAt={createdAt}
                                                createdByName={createdByName}
                                                tone="default"
                                            />
                                        </div>
                                    ) : null}
                                    {showApproveInUnitBody && (
                                        <div className="mb-6 flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => attemptApproveUnit(unit)}
                                                disabled={loadingStates.has(unitId)}
                                                className={confirmActionOutlineButtonClass}
                                            >
                                                {loadingStates.has(unitId) ? (
                                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                                ) : (
                                                    <CheckCircle size={16} className="mr-2" />
                                                )}
                                                Approve
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleDeleteClick(unitId, topics.join(', ') || 'Untitled Unit')}
                                                disabled={loadingStates.has(unitId)}
                                                className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                            >
                                                <Trash2 size={16} className={`mr-2 ${loadingStates.has(unitId) ? 'animate-spin' : ''}`} />
                                                Delete
                                            </Button>
                                        </div>
                                    )}

                                    {approvalBlockedUnitId === unitId && approvalBlockedReason && (
                                        <div className="mb-6 rounded-md border border-[#D4616B]/50 bg-[#D4616B]/5 px-3 py-2 text-sm text-[#BE5059]">
                                            {formatUnitApprovalBlockedMessage(approvalBlockedReason)}
                                        </div>
                                    )}

                                    {/* Lessons — static heading; Regenerate stays on this tab */}
                                    <div>
                                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                            <UnitSectionHeading
                                                title={`Lessons (${lessons.length})`}
                                                className="mb-0"
                                            />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleRegenerateClick(unitId, topics.join(', ') || 'Untitled Unit')}
                                                disabled={loadingStates.has(unitId) || regeneratingUnitId === unitId}
                                                className="text-action-blue border-action-blue/30 hover:bg-action-blue/10"
                                            >
                                                {loadingStates.has(unitId) || regeneratingUnitId === unitId ? (
                                                    <Loader2 size={16} className="mr-2 animate-spin" />
                                                ) : (
                                                    <RefreshCw size={16} className="mr-2" />
                                                )}
                                                Regenerate Unit
                                            </Button>
                                        </div>
                                        <div className="space-y-3">
                                        {lessons.map((lesson) => {
                                            const lessonId = lesson.e251_LessonId || '';
                                            const lessonRowKey = lessonExpandKey(unitId, lessonId);
                                            const isLessonExpanded = expandedLessons.has(lessonRowKey);
                                            const lessonDuration = lesson.e251_LessonDuration || 0;
                                            const isLessonRegenerating = regeneratingLessonKey === `${unitId}-${lessonId}`;
                                            const isRemoveLoading = removeLessonLoadingKeys.has(`${unitId}-${lessonId}`);
                                            const canRemoveLesson = lessons.length > 1;
                                            const lessonCompletedAt = formatIsoDateTimeShort(lesson.e251_LessonCompletionDate ?? null);
                                            const isLessonCompleted = isLessonCompletedDto(lesson);
                                            const coversLabel = lessonCoversLabel(lesson.e251_TopicNames);
                                            const isLessonBusy = isRemoveLoading || isLessonRegenerating;

                                            return (
                                                <Card key={lessonRowKey} className={`bg-white ${isLessonBusy ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    {/* Lesson Header */}
                                                    <div
                                                        onClick={() => toggleLesson(unitId, lessonId)}
                                                        className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                                                    >
                                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                                            <div className="flex min-w-0 flex-1 items-center">
                                                                {isLessonExpanded ? (
                                                                    <ChevronDown size={16} className="text-gray-500 mr-2 shrink-0" />
                                                                ) : (
                                                                    <ChevronRight size={16} className="text-gray-500 mr-2 shrink-0" />
                                                                )}
                                                                <div className="min-w-0 flex-1">
                                                                    <h5 className={cn('text-base font-medium text-gray-900', unitCardExpandableTitleClass)}>
                                                                        {lesson.e251_LessonTitle || 'Untitled Lesson'}
                                                                    </h5>
                                                                    {coversLabel && (
                                                                        <p className="text-sm text-gray-500 mt-0.5">{coversLabel}</p>
                                                                    )}
                                                                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-600 flex-wrap">
                                                                        <Clock size={12} className="mr-1" />
                                                                        {formatTimeDisplay(lessonDuration)}
                                                                        {isLessonCompleted && lessonCompletedAt && (
                                                                            <span className="text-gray-500">• Completed {lessonCompletedAt}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center sm:shrink-0 sm:[&_button]:w-auto [&_button]:w-full"
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handlePrintLesson(lesson)}
                                                                    disabled={isLessonBusy}
                                                                    className="text-gray-600 border-gray-200 hover:bg-gray-50"
                                                                    title="Print lesson"
                                                                >
                                                                    <Printer size={14} className="mr-1" />
                                                                    Print
                                                                </Button>
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        handleRegenerateLessonClick(
                                                                            unitId,
                                                                            lessonId,
                                                                            lesson.e251_LessonTitle || 'Untitled Lesson'
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        isLessonBusy ||
                                                                        loadingStates.has(unitId) ||
                                                                        regeneratingUnitId === unitId ||
                                                                        !!regeneratingMaterialKey
                                                                    }
                                                                    className="text-action-blue border-action-blue/30 hover:bg-action-blue/10"
                                                                    title="Regenerate this lesson"
                                                                >
                                                                    <RefreshCw size={14} className="mr-1" />
                                                                    Regenerate Lesson
                                                                </Button>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        handleRemoveLessonClick(
                                                                            unitId,
                                                                            lessonId,
                                                                            lesson.e251_LessonTitle || 'Untitled Lesson'
                                                                        )
                                                                    }
                                                                    disabled={!canRemoveLesson || isLessonBusy}
                                                                    title={
                                                                        canRemoveLesson
                                                                            ? 'Remove this lesson from the unit'
                                                                            : 'A unit must have at least one lesson'
                                                                    }
                                                                    className="text-destructive-action hover:text-destructive-action-dark hover:bg-destructive-action/10"
                                                                >
                                                                    {isRemoveLoading ? (
                                                                        <Loader2 size={14} className="mr-1 animate-spin" />
                                                                    ) : (
                                                                        <Trash2 size={14} className="mr-1" />
                                                                    )}
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Lesson Content */}
                                                    {isLessonExpanded && (
                                                        <div className="px-4 pb-4 border-t border-gray-100">
                                                            <div className="pt-4 space-y-3">
                                                                {lesson.e251_LessonSummary && (
                                                                    <div>
                                                                        <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                                                                            Summary
                                                                        </h6>
                                                                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{lesson.e251_LessonSummary}</p>
                                                                    </div>
                                                                )}
                                                                {lesson.e251_InstructionSteps && lesson.e251_InstructionSteps.length > 0 && (
                                                                    <div>
                                                                        <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                                                                            Instructions
                                                                        </h6>
                                                                        <ExpandableInstructionStepList
                                                                            steps={lesson.e251_InstructionSteps}
                                                                            defaultExpanded
                                                                        />
                                                                    </div>
                                                                )}
                                                                <LessonSupportingMaterialsSection
                                                                    unitId={unitId}
                                                                    lessonId={lessonId}
                                                                    lessonStarted={isLessonStarted(lesson)}
                                                                    materialsToProvide={lesson.e251_LessonMaterials}
                                                                    materials={resolveLessonMaterials(lesson)}
                                                                    lessonTitle={lesson.e251_LessonTitle}
                                                                    instructionSteps={lesson.e251_InstructionSteps}
                                                                    videoReviewMode={unit.e251_State === UnitStateKey.Reviewing}
                                                                    getSelectedVideoId={(materialId) => {
                                                                        const material = findLessonMaterialById(lesson, materialId);
                                                                        if (!material) return null;
                                                                        return getSelectedVideoId(
                                                                            material,
                                                                            unitId,
                                                                            lessonId,
                                                                            videoSelectionOverrides
                                                                        );
                                                                    }}
                                                                    onVideoSelectionChange={(materialId, videoId) =>
                                                                        handleVideoSelectionChange(
                                                                            unitId,
                                                                            lessonId,
                                                                            lesson,
                                                                            materialId,
                                                                            videoId
                                                                        )
                                                                    }
                                                                    isVideoApproved={(materialId) => {
                                                                        const material = findLessonMaterialById(lesson, materialId);
                                                                        return isVideoApproved(unitId, lessonId, material);
                                                                    }}
                                                                    isVideoApproving={(materialId) => {
                                                                        const material = findLessonMaterialById(lesson, materialId);
                                                                        return isVideoApproving(unitId, lessonId, material);
                                                                    }}
                                                                    onApproveVideos={(materialId) =>
                                                                        handleApproveVideos(unitId, lessonId, lesson, materialId)
                                                                    }
                                                                    isRasterApproved={(stepId, figureKey) => {
                                                                        const step = lesson.e251_InstructionSteps?.find(
                                                                            candidate =>
                                                                                candidate.e251_StepId &&
                                                                                String(candidate.e251_StepId) === String(stepId)
                                                                        );
                                                                        const figure = step?.e251_RasterFigures?.find(
                                                                            candidate => candidate.e251_FigureKey === figureKey
                                                                        );
                                                                        if (!figure) return true;
                                                                        return isRasterApprovedForFigure(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figure
                                                                        );
                                                                    }}
                                                                    isRasterApproving={(stepId, figureKey) =>
                                                                        isRasterApproving(unitId, lessonId, stepId, figureKey)
                                                                    }
                                                                    onApproveRaster={(stepId, figureKey) =>
                                                                        handleApproveRaster(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey
                                                                        )
                                                                    }
                                                                    onRegenerateRaster={(stepId, figureKey, revisionNotes) =>
                                                                        handleRegenerateRaster(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey,
                                                                            revisionNotes
                                                                        )
                                                                    }
                                                                    isRasterRegenerating={(stepId, figureKey) =>
                                                                        isRasterRegenerating(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey
                                                                        )
                                                                    }
                                                                    onAcceptTextOnly={(stepId, figureKey) =>
                                                                        handleAcceptTextOnly(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey
                                                                        )
                                                                    }
                                                                    isAcceptingTextOnly={(stepId, figureKey) =>
                                                                        isAcceptingTextOnly(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey
                                                                        )
                                                                    }
                                                                    onOpenRegenerateUnit={() =>
                                                                        handleRegenerateClick(
                                                                            unitId,
                                                                            topics.join(', ') || 'Untitled Unit'
                                                                        )
                                                                    }
                                                                    onOpenRegenerateLesson={() =>
                                                                        handleRegenerateLessonClick(
                                                                            unitId,
                                                                            lessonId,
                                                                            lesson.e251_LessonTitle || 'Untitled Lesson'
                                                                        )
                                                                    }
                                                                    onUploadRaster={(stepId, figureKey, blobId, title) =>
                                                                        handleUploadRaster(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey,
                                                                            blobId,
                                                                            title
                                                                        )
                                                                    }
                                                                    isRasterUploading={(stepId, figureKey) =>
                                                                        uploadingRasterKeys.has(
                                                                            rasterApprovalStateKey(
                                                                                unitId,
                                                                                lessonId,
                                                                                stepId ?? '',
                                                                                figureKey ?? ''
                                                                            )
                                                                        )
                                                                    }
                                                                    onCheckRasterStatus={(stepId, figureKey) =>
                                                                        handleCheckRasterStatus(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey
                                                                        )
                                                                    }
                                                                    isRasterStatusChecking={(stepId, figureKey) =>
                                                                        isRasterStatusChecking(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey
                                                                        )
                                                                    }
                                                                    isRasterStillGeneratingAfterCheck={(
                                                                        stepId,
                                                                        figureKey
                                                                    ) =>
                                                                        isRasterStillGeneratingAfterCheck(
                                                                            unitId,
                                                                            lessonId,
                                                                            stepId,
                                                                            figureKey
                                                                        )
                                                                    }
                                                                    buildMediaSlotDomId={(kind, materialId, figureKey) =>
                                                                        materialId
                                                                            ? mediaSlotDomId(
                                                                                  unitId,
                                                                                  lessonId,
                                                                                  materialId,
                                                                                  kind,
                                                                                  figureKey
                                                                              )
                                                                            : undefined
                                                                    }
                                                                    onRegenerateMaterial={material =>
                                                                        handleRegenerateMaterialClick(
                                                                            unitId,
                                                                            lessonId,
                                                                            material
                                                                        )
                                                                    }
                                                                    isMaterialRegenerating={materialId =>
                                                                        regeneratingMaterialKey === materialId
                                                                    }
                                                                />
                                                                {sessionDebugMode && (
                                                                    <div className="space-y-2 pt-2 border-t border-gray-100">
                                                                        <h6 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                                                            Debug: Assigned topic figures
                                                                        </h6>

                                                                        {(lesson.e251_AssignedTopicFigures ?? []).length === 0 ? (
                                                                            <p className="text-sm text-gray-500">None</p>
                                                                        ) : (
                                                                            <div className="space-y-2 text-xs text-gray-700">
                                                                                {lesson.e251_AssignedTopicFigures?.map((af, idx) => {
                                                                                    const topicId = af?.e251_TopicId ?? '';
                                                                                    const topicIdx = (lesson.e251_TopicIds ?? []).findIndex(
                                                                                        tid => String(tid) === String(topicId)
                                                                                    );
                                                                                    const topicName =
                                                                                        topicIdx >= 0
                                                                                            ? lesson.e251_TopicNames?.[topicIdx] ?? null
                                                                                            : null;

                                                                                    const worksheetStepId = af?.e251_WorksheetStepId ?? null;
                                                                                    const worksheetHostStepNumber = worksheetStepId
                                                                                        ? lesson.e251_InstructionSteps?.find(
                                                                                              s =>
                                                                                                  s?.e251_StepId &&
                                                                                                  String(s.e251_StepId) ===
                                                                                                      String(worksheetStepId)
                                                                                          )?.e251_StepNumber
                                                                                        : undefined;

                                                                                    return (
                                                                                        <details
                                                                                            key={`${topicId}-${worksheetStepId ?? 'none'}-${idx}`}
                                                                                        >
                                                                                            <summary className="cursor-pointer select-none">
                                                                                                {topicName ?? `Topic ${topicId}`}
                                                                                            </summary>
                                                                                            <div className="mt-2 rounded border border-gray-100 bg-gray-50 p-2 whitespace-pre-wrap">
                                                                                                Reading: {af?.e251_ReadingFigureKind ?? 'unknown'}
                                                                                                {af?.e251_ReadingRegisteredFigureType
                                                                                                    ? ` (${af?.e251_ReadingRegisteredFigureType})`
                                                                                                    : ''}
                                                                                                {af?.e251_ReadingReason
                                                                                                    ? `\nReason: ${af?.e251_ReadingReason}`
                                                                                                    : ''}

                                                                                                {'\n'}
                                                                                                Worksheet: {af?.e251_WorksheetFigureKind ?? 'unknown'}
                                                                                                {af?.e251_WorksheetFigureType
                                                                                                    ? ` (${af?.e251_WorksheetFigureType})`
                                                                                                    : ''}

                                                                                                {'\n'}
                                                                                                WorksheetHostStep: {worksheetHostStepNumber ?? 'unresolved'}
                                                                                                {worksheetStepId
                                                                                                    ? ` (stepId: ${worksheetStepId})`
                                                                                                    : ' (none)'}
                                                                                            </div>
                                                                                        </details>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </Card>
                                            );
                                        })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </Card>
                );
            })}

            {/* Regenerate Unit Dialog */}
            {regeneratingUnit && (
                <RegenerateUnitDialog
                    unitId={regeneratingUnit.unitId}
                    unitTitle={regeneratingUnit.unitTitle}
                    onClose={(wasConfirmed) => handleRegenerateComplete(wasConfirmed, regeneratingUnit.unitId)}
                />
            )}

            {regeneratingLesson && (
                <RegenerateLessonDialog
                    unitId={regeneratingLesson.unitId}
                    lessonId={regeneratingLesson.lessonId}
                    lessonTitle={regeneratingLesson.lessonTitle}
                    onClose={(wasConfirmed) =>
                        handleRegenerateLessonComplete(
                            wasConfirmed,
                            regeneratingLesson.unitId,
                            regeneratingLesson.lessonId
                        )
                    }
                />
            )}

            {regeneratingMaterial && (
                <RegenerateLessonMaterialDialog
                    unitId={regeneratingMaterial.unitId}
                    lessonId={regeneratingMaterial.lessonId}
                    stepId={regeneratingMaterial.stepId}
                    materialType={regeneratingMaterial.materialType}
                    materialTitle={regeneratingMaterial.materialTitle}
                    onClose={(wasConfirmed) =>
                        handleRegenerateMaterialComplete(
                            wasConfirmed,
                            regeneratingMaterial.materialId
                        )
                    }
                />
            )}

            {/* Delete Unit Dialog */}
            {deletingUnit && (
                <DeleteUnitDialog
                    unitId={deletingUnit.unitId}
                    unitTitle={deletingUnit.unitTitle}
                    onConfirm={() => handleDeleteConfirm(deletingUnit.unitId)}
                    onClose={handleDeleteComplete}
                />
            )}

            {removingLesson && (
                <RemoveLessonDialog
                    unitId={removingLesson.unitId}
                    lessonId={removingLesson.lessonId}
                    lessonTitle={removingLesson.lessonTitle}
                    onConfirm={() =>
                        handleRemoveLessonConfirm(removingLesson.unitId, removingLesson.lessonId)
                    }
                    onRemoved={() =>
                        applyLessonRemovalToPanelState(
                            removingLesson.unitId,
                            removingLesson.lessonId
                        )
                    }
                    onClose={handleRemoveLessonClose}
                />
            )}

            {/* Wait loader while regenerating */}
            {regeneratingUnitId && (
                <Dialog open onOpenChange={() => {}}>
                    <DialogContent dismissible={false} className="max-w-2xl sm:max-w-2xl">
                        <ThinkingAnimation
                            message="Regenerating unit"
                            progressSteps={regeneratingUnitProgress?.stages}
                            currentProgressStepIndex={regeneratingUnitProgress?.stageIndex ?? 0}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {regeneratingLessonKey && (
                <Dialog open onOpenChange={() => {}}>
                    <DialogContent dismissible={false} className="max-w-2xl sm:max-w-2xl">
                        <ThinkingAnimation
                            message="Regenerating lesson"
                            progressSteps={regeneratingLessonProgress?.stages}
                            currentProgressStepIndex={regeneratingLessonProgress?.stageIndex ?? 0}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {regeneratingMaterialKey && (
                <Dialog open onOpenChange={() => {}}>
                    <DialogContent dismissible={false} className="max-w-2xl sm:max-w-2xl">
                        <ThinkingAnimation
                            message="Regenerating material"
                            progressSteps={regeneratingMaterialProgress?.stages}
                            currentProgressStepIndex={regeneratingMaterialProgress?.stageIndex ?? 0}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
    }
}

